import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Pencil,
    Trash2,
    RefreshCw,
    Image,
    X,
    Save,
    Calendar,
    Link,
    Eye,
    EyeOff,
} from 'lucide-react';
import Notification from '../Common/Notification';
import { storefrontGet, storefrontPost, storefrontPut, storefrontDelete } from '../../services/storefrontApi';
import AppleSelect from '../Common/AppleSelect';
import {tabClass} from '../Common/TabBar';
import ActionMenu from '../Common/ActionMenu';

const PLACEMENTS = [
    { key: 'hero',    label: 'Hero (slider principal)' },
    { key: 'sidebar', label: 'Barre latérale' },
    { key: 'inline',  label: 'Inline (dans la page)' },
    { key: 'popup',   label: 'Popup' },
    { key: 'footer',  label: 'Pied de page' },
];

/* ─── Banner editor ─────────────────────────────────────────────────────── */
const BannerEditor = ({ banner, onClose, onSave, isSaving }) => {
    const isEdit = !!banner?.id;
    const toDateInput = (iso) => iso ? iso.slice(0, 16) : '';

    const [form, setForm] = useState({
        title:        banner?.title        || '',
        description:  banner?.description  || '',
        image:        banner?.image        || '',
        mobile_image: banner?.mobile_image || '',
        link_url:     banner?.link_url     || '',
        link_text:    banner?.link_text    || '',
        placement:    banner?.placement    || 'hero',
        position:     banner?.position     ?? 0,
        active:       banner?.active       ?? true,
        starts_at:    toDateInput(banner?.starts_at),
        ends_at:      toDateInput(banner?.ends_at),
        style: {
            background_color: banner?.style?.background_color || '#1A1A2E',
            text_color:       banner?.style?.text_color       || '#FFFFFF',
            ...(banner?.style || {}),
        },
    });

    const set    = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const setSty = (k, v) => setForm(f => ({ ...f, style: { ...f.style, [k]: v } }));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                        {isEdit ? 'Modifier la bannière' : 'Nouvelle bannière'}
                    </h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 py-5 space-y-4 max-h-[78vh] overflow-y-auto">
                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Titre *</label>
                        <input type="text" value={form.title} onChange={e => set('title', e.target.value)}
                            placeholder="Offre spéciale Ramadan"
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Description</label>
                        <textarea value={form.description} onChange={e => set('description', e.target.value)}
                            rows={2} placeholder="-30% sur tous les tajines"
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary resize-none" />
                    </div>

                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Image (URL) *</label>
                        <input type="url" value={form.image} onChange={e => set('image', e.target.value)}
                            placeholder="https://cdn.example.com/banner.jpg"
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-500 mb-1.5 block">Image mobile (URL)</label>
                        <input type="url" value={form.mobile_image} onChange={e => set('mobile_image', e.target.value)}
                            placeholder="https://cdn.example.com/banner-mobile.jpg"
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-gray-500 mb-1.5 block">Lien URL</label>
                            <input type="text" value={form.link_url} onChange={e => set('link_url', e.target.value)}
                                placeholder="/products?category=tajines"
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 mb-1.5 block">Texte du lien</label>
                            <input type="text" value={form.link_text} onChange={e => set('link_text', e.target.value)}
                                placeholder="Profiter de l'offre"
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-gray-500 mb-1.5 block">Emplacement</label>
                            <AppleSelect value={form.placement} onChange={e => set('placement', e.target.value)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary">
                                {PLACEMENTS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                            </AppleSelect>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-500 mb-1.5 block">Position</label>
                            <input type="number" min={0} value={form.position} onChange={e => set('position', parseInt(e.target.value, 10) || 0)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 p-4">
                        <p className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                            <Calendar size={13} /> Planification
                        </p>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Date de début</label>
                            <input type="datetime-local" value={form.starts_at} onChange={e => set('starts_at', e.target.value)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Date de fin</label>
                            <input type="datetime-local" value={form.ends_at} onChange={e => set('ends_at', e.target.value)}
                                className="w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-sm dark:bg-gray-900 dark:text-white outline-none focus:border-primary" />
                        </div>
                    </div>

                    {/* Style */}
                    <div className="grid grid-cols-2 gap-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 p-4">
                        <p className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Style</p>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Fond</label>
                            <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 bg-white dark:bg-gray-900">
                                <input type="color" value={form.style.background_color} onChange={e => setSty('background_color', e.target.value)}
                                    className="w-6 h-6 rounded border-0 p-0 cursor-pointer" />
                                <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{form.style.background_color}</span>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Texte</label>
                            <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 bg-white dark:bg-gray-900">
                                <input type="color" value={form.style.text_color} onChange={e => setSty('text_color', e.target.value)}
                                    className="w-6 h-6 rounded border-0 p-0 cursor-pointer" />
                                <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{form.style.text_color}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between py-2 border-t border-gray-100 dark:border-gray-700">
                        <span className="text-sm text-gray-700 dark:text-gray-200">Actif</span>
                        <button type="button" onClick={() => set('active', !form.active)}
                            className={`relative h-6 w-11 rounded-full transition-colors ${form.active ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}>
                            <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${form.active ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                    </div>
                </div>

                <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        Annuler
                    </button>
                    <button
                        onClick={() => onSave(form)}
                        disabled={isSaving || !form.title}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 disabled:opacity-50"
                    >
                        {isSaving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── Banner card ───────────────────────────────────────────────────────── */
const BannerCard = ({ banner, onEdit, onDelete, onToggle }) => {
    const placementLabel = PLACEMENTS.find(p => p.key === banner.placement)?.label || banner.placement;
    const now = new Date();
    const start = banner.starts_at ? new Date(banner.starts_at) : null;
    const end   = banner.ends_at   ? new Date(banner.ends_at)   : null;
    const scheduled = start && start > now;
    const expired   = end && end < now;

    return (
        <div className={`bg-white dark:bg-gray-800 border rounded-xl overflow-hidden group transition-opacity ${!banner.active ? 'opacity-60' : ''} border-gray-200 dark:border-gray-700`}>
            {banner.image && (
                <div className="h-32 bg-gray-100 dark:bg-gray-700 overflow-hidden">
                    <img src={banner.image} alt={banner.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
            )}
            <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <p className="font-medium text-sm text-gray-800 dark:text-white truncate">{banner.title}</p>
                        {banner.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{banner.description}</p>}
                    </div>
                    <ActionMenu
                        label={`Actions pour ${banner.title || ''}`}
                        items={[
                            {label: banner.active ? 'Masquer' : 'Afficher', icon: banner.active ? EyeOff : Eye, onClick: () => onToggle(banner)},
                            {label: 'Modifier', icon: Pencil, onClick: () => onEdit(banner)},
                            {divider: true},
                            {label: 'Supprimer', icon: Trash2, danger: true, onClick: () => onDelete(banner)},
                        ]}
                    />
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{placementLabel}</span>
                    {scheduled && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Planifiée</span>}
                    {expired   && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Expirée</span>}
                    {!scheduled && !expired && banner.active && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>}
                    {banner.link_url && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary flex items-center gap-1">
                            <Link size={10} /> Lien
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ─── Main component ─────────────────────────────────────────────────────── */
const BannersSettings = () => {
    const [activePlacement, setActivePlacement] = useState('hero');
    const [banners, setBanners] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editorBanner, setEditorBanner] = useState(undefined);
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
        if (type !== 'loading') setTimeout(() => setNotification(n => ({ ...n, show: false })), 3000);
    };

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await storefrontGet('/banners', { placement: activePlacement });
            const data = res.data?.data ?? res.data ?? [];
            setBanners(Array.isArray(data) ? data : []);
        } catch {
            showNotification('error', 'Impossible de charger les bannières.');
        } finally {
            setIsLoading(false);
        }
    }, [activePlacement]);

    useEffect(() => { load(); }, [load]);

    const handleSave = async (form) => {
        setIsSaving(true);
        try {
            const payload = {
                ...form,
                starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
                ends_at:   form.ends_at   ? new Date(form.ends_at).toISOString()   : null,
            };
            if (editorBanner?.id) {
                await storefrontPut(`/banners/${editorBanner.id}`, payload);
                showNotification('success', 'Bannière mise à jour.');
            } else {
                await storefrontPost('/banners', payload);
                showNotification('success', 'Bannière créée.');
            }
            setEditorBanner(undefined);
            load();
        } catch {
            showNotification('error', 'Erreur lors de l\'enregistrement.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (banner) => {
        if (!window.confirm(`Supprimer la bannière « ${banner.title} » ?`)) return;
        try {
            await storefrontDelete(`/banners/${banner.id}`);
            showNotification('success', 'Bannière supprimée.');
            load();
        } catch {
            showNotification('error', 'Impossible de supprimer cette bannière.');
        }
    };

    const handleToggle = async (banner) => {
        try {
            await storefrontPut(`/banners/${banner.id}`, { active: !banner.active });
            setBanners(prev => prev.map(b => b.id === banner.id ? { ...b, active: !b.active } : b));
        } catch {
            showNotification('error', 'Erreur lors de la mise à jour.');
        }
    };

    return (
        <div className="px-4 py-6 max-w-5xl mx-auto">
            <Notification show={notification.show} type={notification.type} message={notification.message} onClose={() => setNotification(n => ({ ...n, show: false }))} />

            {/* Placement tabs */}
            <div className="flex overflow-x-auto no-scrollbar mb-6 border-b border-gray-200 dark:border-gray-700">
                {PLACEMENTS.map(p => (
                    <button
                        key={p.key}
                        onClick={() => setActivePlacement(p.key)}
                        className={`${tabClass(activePlacement === p.key)} -mb-px`}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">{banners.length} bannière(s)</p>
                <button
                    onClick={() => setEditorBanner(null)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 transition-colors"
                >
                    <Plus size={16} /> Ajouter une bannière
                </button>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <RefreshCw size={20} className="animate-spin text-primary" />
                </div>
            ) : banners.length === 0 ? (
                <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                    <Image size={40} className="mx-auto mb-3 opacity-20" />
                    <p className="text-sm">Aucune bannière pour cet emplacement.</p>
                    <button onClick={() => setEditorBanner(null)} className="mt-3 text-primary text-sm hover:underline">
                        Créer la première bannière
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...banners].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)).map(b => (
                        <BannerCard key={b.id} banner={b} onEdit={setEditorBanner} onDelete={handleDelete} onToggle={handleToggle} />
                    ))}
                </div>
            )}

            {editorBanner !== undefined && (
                <BannerEditor
                    banner={editorBanner}
                    onClose={() => setEditorBanner(undefined)}
                    onSave={handleSave}
                    isSaving={isSaving}
                />
            )}
        </div>
    );
};

export default BannersSettings;
